/**
 * Whitespace Remover Tool
 *
 * Remove or normalize whitespace with multiple options.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const WhitespaceRemoverTool: ITransformerTool = {
  id: 'whitespace-remover',
  name: 'Whitespace Remover',
  description: 'Remove or normalize whitespace — trim lines, collapse spaces, remove blank lines',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['whitespace', 'remove', 'trim', 'collapse', 'blank lines', 'normalize', 'strip'],

  seo: {
    slug: 'whitespace-remover',
    pageTitle: 'Whitespace Remover - Free Online Tool | SWE DevTools',
    metaDescription: 'Remove or normalize whitespace in text. Trim lines, collapse spaces, remove blank lines, or strip all whitespace. Fast, private, runs in your browser.',
    keywords: ['whitespace remover', 'remove whitespace online', 'trim whitespace', 'collapse spaces', 'remove blank lines'],
    relatedTools: ['deduplicate-lines', 'remove-empty-lines', 'find-replace'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'trimLines',
      type: 'boolean',
      label: 'Trim Lines',
      defaultValue: true,
      description: 'Remove leading and trailing whitespace from each line',
    },
    {
      name: 'collapseSpaces',
      type: 'boolean',
      label: 'Collapse Spaces',
      defaultValue: false,
      description: 'Collapse multiple consecutive spaces into a single space',
    },
    {
      name: 'removeBlankLines',
      type: 'boolean',
      label: 'Remove Blank Lines',
      defaultValue: false,
      description: 'Remove lines that are empty or contain only whitespace',
    },
    {
      name: 'removeAllWhitespace',
      type: 'boolean',
      label: 'Remove All Whitespace',
      defaultValue: false,
      description: 'Remove every whitespace character (overrides other options)',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '');
    if (!text) return '';

    const removeAll = config.removeAllWhitespace === true;
    const trimLines = config.trimLines !== false;
    const collapseSpaces = config.collapseSpaces === true;
    const removeBlankLines = config.removeBlankLines === true;

    if (removeAll) {
      return text.replace(/\s/g, '');
    }

    let lines = text.split('\n');

    if (trimLines) {
      lines = lines.map((line) => line.trim());
    }

    if (collapseSpaces) {
      lines = lines.map((line) => line.replace(/ {2,}/g, ' '));
    }

    if (removeBlankLines) {
      lines = lines.filter((line) => line.trim().length > 0);
    }

    return lines.join('\n');
  },
};
